import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GameStyles.css';

const InductiveGame = () => {
  const [level, setLevel] = useState(1);
  const [points, setPoints] = useState(0);
  const [time, setTime] = useState(30);
  const [gameStarted, setGameStarted] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const [exampleGrids, setExampleGrids] = useState([]);
  const [optionGrids, setOptionGrids] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const shapes = ['circle', 'square', 'triangle', 'cross', 'add', 'divide', 'minus', 'equal'];
  const gridSize = 3;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleTimerEnd = useCallback(() => {
    alert(`Game Over! Your final score: ${points}`);
    setGameStarted(false);
    setLevel(1);
    setPoints(0);
  }, [points]);

  useEffect(() => {
    if (gameStarted && !gamePaused) {
      timerRef.current = setInterval(() => {
        setTime((prev) => {
          if (prev <= 1) {
            handleTimerEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameStarted, gamePaused, handleTimerEnd]);

  const shuffle = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const generateRandomGrid = (limitedShapes = null) => {
    // Select 3-4 unique shapes for this grid if not provided
    let availableShapes = limitedShapes;
    if (!availableShapes) {
      const numShapes = Math.random() < 0.5 ? 3 : 4; // Randomly choose 3 or 4 shapes
      const shuffledShapes = shuffle([...shapes]);
      availableShapes = shuffledShapes.slice(0, numShapes);
    }

    const grid = [];
    for (let i = 0; i < gridSize; i++) {
      const row = [];
      for (let j = 0; j < gridSize; j++) {
        row.push(availableShapes[Math.floor(Math.random() * availableShapes.length)]);
      }
      grid.push(row);
    }
    return grid;
  };

  // Pattern validation functions
  const hasPattern = (grid, patternType, patternShape) => {
    switch (patternType) {
      case 'fourCorners':
        // All 4 corners have the same shape
        return grid[0][0] === patternShape &&
               grid[0][2] === patternShape &&
               grid[2][0] === patternShape &&
               grid[2][2] === patternShape;

      case 'center':
        // Center cell has specific shape
        return grid[1][1] === patternShape;

      case 'plusSign':
        // Plus sign pattern: center + middle of each edge
        return grid[1][1] === patternShape &&
               grid[0][1] === patternShape &&
               grid[1][0] === patternShape &&
               grid[1][2] === patternShape &&
               grid[2][1] === patternShape;

      case 'xSign':
        // X pattern: all 4 corners + center
        return grid[0][0] === patternShape &&
               grid[0][2] === patternShape &&
               grid[1][1] === patternShape &&
               grid[2][0] === patternShape &&
               grid[2][2] === patternShape;

      case 'topRow':
        // Top row all same shape
        return grid[0][0] === patternShape &&
               grid[0][1] === patternShape &&
               grid[0][2] === patternShape;

      case 'middleRow':
        // Middle row all same shape
        return grid[1][0] === patternShape &&
               grid[1][1] === patternShape &&
               grid[1][2] === patternShape;

      case 'bottomRow':
        // Bottom row all same shape
        return grid[2][0] === patternShape &&
               grid[2][1] === patternShape &&
               grid[2][2] === patternShape;

      case 'leftColumn':
        // Left column all same shape
        return grid[0][0] === patternShape &&
               grid[1][0] === patternShape &&
               grid[2][0] === patternShape;

      case 'middleColumn':
        // Middle column all same shape
        return grid[0][1] === patternShape &&
               grid[1][1] === patternShape &&
               grid[2][1] === patternShape;

      case 'rightColumn':
        // Right column all same shape
        return grid[0][2] === patternShape &&
               grid[1][2] === patternShape &&
               grid[2][2] === patternShape;

      case 'leftDiagonal':
        // Left diagonal (top-left to bottom-right)
        return grid[0][0] === patternShape &&
               grid[1][1] === patternShape &&
               grid[2][2] === patternShape;

      case 'rightDiagonal':
        // Right diagonal (top-right to bottom-left)
        return grid[0][2] === patternShape &&
               grid[1][1] === patternShape &&
               grid[2][0] === patternShape;

      default:
        return false;
    }
  };

  const generateGridWithPattern = (patternType, patternShape) => {
    // Select 3-4 unique shapes for this grid, ensuring pattern shape is included
    const numShapes = Math.random() < 0.5 ? 3 : 4; // Randomly choose 3 or 4 shapes
    const otherShapes = shapes.filter(s => s !== patternShape);
    const shuffledOthers = shuffle([...otherShapes]);
    const limitedShapes = [patternShape, ...shuffledOthers.slice(0, numShapes - 1)];

    // Create a grid with the specified pattern using limited shapes
    const grid = generateRandomGrid(limitedShapes);

    // Apply the pattern by setting specific cells to the pattern shape
    switch (patternType) {
      case 'fourCorners':
        grid[0][0] = patternShape;
        grid[0][2] = patternShape;
        grid[2][0] = patternShape;
        grid[2][2] = patternShape;
        break;

      case 'center':
        grid[1][1] = patternShape;
        break;

      case 'plusSign':
        grid[1][1] = patternShape;
        grid[0][1] = patternShape;
        grid[1][0] = patternShape;
        grid[1][2] = patternShape;
        grid[2][1] = patternShape;
        break;

      case 'xSign':
        grid[0][0] = patternShape;
        grid[0][2] = patternShape;
        grid[1][1] = patternShape;
        grid[2][0] = patternShape;
        grid[2][2] = patternShape;
        break;

      case 'topRow':
        grid[0][0] = patternShape;
        grid[0][1] = patternShape;
        grid[0][2] = patternShape;
        break;

      case 'middleRow':
        grid[1][0] = patternShape;
        grid[1][1] = patternShape;
        grid[1][2] = patternShape;
        break;

      case 'bottomRow':
        grid[2][0] = patternShape;
        grid[2][1] = patternShape;
        grid[2][2] = patternShape;
        break;

      case 'leftColumn':
        grid[0][0] = patternShape;
        grid[1][0] = patternShape;
        grid[2][0] = patternShape;
        break;

      case 'middleColumn':
        grid[0][1] = patternShape;
        grid[1][1] = patternShape;
        grid[2][1] = patternShape;
        break;

      case 'rightColumn':
        grid[0][2] = patternShape;
        grid[1][2] = patternShape;
        grid[2][2] = patternShape;
        break;

      case 'leftDiagonal':
        grid[0][0] = patternShape;
        grid[1][1] = patternShape;
        grid[2][2] = patternShape;
        break;

      case 'rightDiagonal':
        grid[0][2] = patternShape;
        grid[1][1] = patternShape;
        grid[2][0] = patternShape;
        break;
      default:
        break;
    }

    return grid;
  };

  const createLevel = () => {
    // Available pattern types
    const patternTypes = [
      'fourCorners', 'center', 'plusSign', 'xSign',
      'topRow', 'middleRow', 'bottomRow',
      'leftColumn', 'middleColumn', 'rightColumn',
      'leftDiagonal', 'rightDiagonal'
    ];

    // Choose a random pattern type
    const selectedPattern = patternTypes[Math.floor(Math.random() * patternTypes.length)];

    // Choose a random shape for this pattern
    const patternShape = shapes[Math.floor(Math.random() * shapes.length)];

    // Generate 2 example grids that both have the same pattern with the same shape
    const grid1 = generateGridWithPattern(selectedPattern, patternShape);
    const grid2 = generateGridWithPattern(selectedPattern, patternShape);

    setExampleGrids([grid1, grid2]);

    // Create 4 options (single grids): 2 correct and 2 incorrect
    const options = [];

    // Create 2 correct options (grids that HAVE the same pattern with same shape)
    for (let i = 0; i < 2; i++) {
      const correctGrid = generateGridWithPattern(selectedPattern, patternShape);
      options.push({ grid: correctGrid, isCorrect: true });
    }

    // Create 2 incorrect options (grids that DON'T have the pattern)
    for (let i = 0; i < 2; i++) {
      let incorrectGrid = generateRandomGrid();
      // Make sure it doesn't accidentally have the correct pattern
      let attempts = 0;
      while (hasPattern(incorrectGrid, selectedPattern, patternShape) && attempts < 10) {
        incorrectGrid = generateRandomGrid();
        attempts++;
      }
      options.push({ grid: incorrectGrid, isCorrect: false });
    }

    // Shuffle options
    const shuffledOptions = shuffle(options);
    setOptionGrids(shuffledOptions);

    // Store correct answer indices
    const correctIndices = [];
    shuffledOptions.forEach((opt, idx) => {
      if (opt.isCorrect) correctIndices.push(idx);
    });
    setCorrectAnswers(correctIndices);
    setSelectedOptions([]);
    setFeedback(null);
    setTime(30);
  };

  const handleStartGame = () => {
    setGameStarted(true);
    setLevel(1);
    setPoints(0);
    createLevel();
  };

  const handleOptionClick = (index) => {
    if (feedback) return; // Prevent clicks during feedback

    if (selectedOptions.includes(index)) {
      // Deselect
      setSelectedOptions(selectedOptions.filter(i => i !== index));
    } else {
      // Select (max 2 selections)
      if (selectedOptions.length < 2) {
        const newSelections = [...selectedOptions, index];
        setSelectedOptions(newSelections);

        // Auto-check when 2 options are selected
        if (newSelections.length === 2) {
          setTimeout(() => checkAnswer(newSelections), 300);
        }
      }
    }
  };

  const checkAnswer = (selections) => {
    // Check if both selections are correct
    const sortedSelections = [...selections].sort();
    const sortedCorrect = [...correctAnswers].sort();

    const isCorrect = sortedSelections.length === 2 &&
                      sortedSelections[0] === sortedCorrect[0] &&
                      sortedSelections[1] === sortedCorrect[1];

    if (isCorrect) {
      setFeedback('correct');
      setPoints(prev => prev + 3);

      setTimeout(() => {
        setFeedback(null);
        setLevel(prev => prev + 1);
        createLevel();
      }, 1000);
    } else {
      setFeedback('wrong');
      setPoints(prev => Math.max(0, prev - 1));

      setTimeout(() => {
        setFeedback(null);
        setSelectedOptions([]);
      }, 1000);
    }
  };


  const togglePause = () => {
    setGamePaused(prev => !prev);
  };

  const toggleMusic = () => {
    setMusicEnabled(prev => !prev);
    if (audioRef.current) {
      if (musicEnabled) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  return (
    <div className="game-container inductive-game">
      <audio ref={audioRef} loop>
        <source src="/assets/inductive/audio/audio.mp3" type="audio/mpeg" />
      </audio>

      {!gameStarted ? (
        <div className="rules-container">
          <h1 className="game-title">Inductive Challenge</h1>
          <div className="rules-content">
            <p>
              Observe the two example grids on the left side. Both grids contain the same visual pattern
              with specific shapes. The pattern could be in rows, columns, diagonals, corners, or form
              special shapes like a plus sign or X. On the right side, you'll see four option grids.
              Your task is to identify which TWO of these option grids contain the SAME pattern with
              the SAME shape as the examples.
            </p>
            <h3>How to Play</h3>
            <ul>
              <li>Study both example grids to identify the common pattern (left side)</li>
              <li>Look for patterns like: same rows, columns, diagonals, four corners, plus sign, X sign</li>
              <li>Click to select 2 options from the 4 grids (right side)</li>
              <li>The correct 2 options have the same pattern with the same shape</li>
              <li>Correct answer: +3 points</li>
              <li>Wrong answer: -1 point</li>
              <li>You have 30 seconds per level</li>
            </ul>
            <button className="play-button" onClick={handleStartGame}>
              Play
            </button>
          </div>
        </div>
      ) : (
        <div className="game-play-area">
          <div className="game-header">
            <div className="control-buttons">
              <button onClick={togglePause} className="control-btn">
                {gamePaused ? '▶️' : '⏸️'}
              </button>
              <button onClick={toggleMusic} className="control-btn">
                {musicEnabled ? '🔊' : '🔇'}
              </button>
            </div>
            <div className="game-stat">Level {level}</div>
            <div className="game-stat">Points: {points}</div>
            <div className="game-stat">Time: {time}s</div>
            <div className="game-name">Inductive Game</div>
          </div>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(time / 30) * 100}%` }}></div>
          </div>

          <div className="game-content inductive-content">
            {/* Feedback Animation */}
            {feedback && (
              <div className={`feedback-animation ${feedback}`}>
                {feedback === 'correct' ? (
                  <>
                    <img src="/assets/inductive/images/correct.png" alt="Correct!" className="feedback-icon" />
                    <span className="feedback-text">Correct! +3</span>
                  </>
                ) : (
                  <>
                    <img src="/assets/inductive/images/wrong.png" alt="Wrong!" className="feedback-icon" />
                    <span className="feedback-text">Wrong! -1</span>
                  </>
                )}
              </div>
            )}

            <div className="inductive-layout">
              {/* Left side - Example Grids */}
              <div className="example-section">
                <h3 className="section-title">Pattern Examples</h3>
                <div className="example-grids">
                  {exampleGrids.map((grid, gridIndex) => (
                    <div key={gridIndex} className="pattern-grid-wrapper">
                      <div className="pattern-grid">
                        {grid.map((row, rowIndex) => (
                          row.map((shape, colIndex) => (
                            <div key={`${rowIndex}-${colIndex}`} className="pattern-cell">
                              <img src={`/assets/inductive/images/${shape}.png`} alt={shape} />
                            </div>
                          ))
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="pattern-hint">☝️ Observe the pattern change</p>
              </div>

              {/* Vertical Divider */}
              <div className="vertical-divider"></div>

              {/* Right side - Option Grids */}
              <div className="options-section">
                <h3 className="section-title">Which 2 follow the pattern?</h3>
                <div className="options-grid">
                  {optionGrids.map((option, optIndex) => (
                    <div
                      key={optIndex}
                      className={`option-wrapper ${selectedOptions.includes(optIndex) ? 'selected' : ''} ${
                        feedback && correctAnswers.includes(optIndex) ? 'correct-answer' : ''
                      } ${
                        feedback && selectedOptions.includes(optIndex) && !correctAnswers.includes(optIndex) ? 'wrong-answer' : ''
                      }`}
                      onClick={() => handleOptionClick(optIndex)}
                    >
                      <div className="option-label">Option {optIndex + 1}</div>
                      <div className="pattern-grid option-grid">
                        {option.grid.map((row, rowIndex) => (
                          row.map((shape, colIndex) => (
                            <div key={`${rowIndex}-${colIndex}`} className="pattern-cell">
                              <img src={`/assets/inductive/images/${shape}.png`} alt={shape} />
                            </div>
                          ))
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="inductive-instructions">
              {selectedOptions.length === 0 && <p>👆 Click to select 2 options that follow the same pattern</p>}
              {selectedOptions.length === 1 && <p>✅ Select 1 more option</p>}
              {selectedOptions.length === 2 && !feedback && <p>⏳ Checking answer...</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InductiveGame;
